import React from 'react';
import { Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { clickableCardSx, RADIUS, staticCardSx } from '../../../theme/tokens';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useParams } from 'react-router-dom';
import { useRouteNavigation } from '../../../hooks/useRouteNavigation';

interface QuickAccessCardProps {
  title: string;
  description?: string;
  icon: IconName;
  href?: string;
  /** Atalhos da home customizada: abre em nova aba mesmo quando o link é interno. */
  openInNewTab?: boolean;
}

/** Comporta 2 linhas de título + 2 de descrição + padding, para todos os cards terem a mesma altura. */
const CARD_HEIGHT = 104;

export default function QuickAccessCard({ title, description, icon, href = '#', openInNewTab = false }: QuickAccessCardProps) {
  const navigateWithPrefix = useRouteNavigation();
  const params = useParams<{ state?: string; city?: string }>();
  const isDisabled = href === '#';
  const isExternalLink = href?.startsWith('http');
  const opensNewTab = !isDisabled && (isExternalLink || openInNewTab);
  const isInternalRoute = !isDisabled && !isExternalLink && !openInNewTab;

  const getHref = () => {
    if (isDisabled) return undefined;
    if (isExternalLink) return href;

    // Gera o href com prefixo se estamos em uma rota com state/city
    const cleanPath = href.startsWith('/') ? href.substring(1) : href;
    if (params.state && params.city) {
      return `/${params.state}/${params.city}/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    if (isInternalRoute) {
      e.preventDefault();
      navigateWithPrefix(href);
    }
    // Para links externos, deixa o comportamento padrão do navegador
  };

  return (
    <Box
      component="a"
      href={getHref()}
      target={opensNewTab ? '_blank' : undefined}
      rel={opensNewTab ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      aria-disabled={isDisabled || undefined}
      sx={{
        ...(isDisabled ? staticCardSx : clickableCardSx) as object,
        // Altura fixa (título e descrição com até 2 linhas cada): no flex-wrap cada linha teria altura própria
        height: CARD_HEIGHT,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        pr: 2,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.55 : 1,
        textDecoration: 'none',
        color: 'text.primary',
        '&:hover .icon-wrapper': {
          backgroundColor: isDisabled ? undefined : 'brand.surfaceStrong',
        },
      }}
    >
      <Box
        className="icon-wrapper"
        aria-hidden
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: `${RADIUS.card - 2}px`,
          backgroundColor: 'brand.surface',
          color: 'brand.text',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 200ms',
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ fontSize: '1.125rem' }} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '0.9375rem',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
          {opensNewTab && <Box component="span" sx={visuallyHidden}> (abre em nova aba)</Box>}
        </Typography>
        {description && (
          <Typography
            component="span"
            sx={{
              mt: 0.25,
              color: 'text.secondary',
              fontSize: '0.8125rem',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Itens por linha em cada breakpoint e espaço entre eles (px). */
const GRID_COLUMNS = { xs: 2, sm: 3, md: 4, lg: 5 } as const;
const GRID_GAP = { xs: 12, sm: 12, md: 16, lg: 16 } as const;

const itemWidth = (bp: keyof typeof GRID_COLUMNS) => {
  const cols = GRID_COLUMNS[bp];
  const gap = GRID_GAP[bp];
  return `calc((100% - ${(cols - 1) * gap}px) / ${cols})`;
};

/**
 * Grade dos atalhos: 2 colunas no mobile, 3 no tablet, 4 a 5 no desktop.
 * Usa flex-wrap centralizado para que a última linha incompleta fique no meio, e não presa à esquerda;
 * itens da linha com a mesma altura.
 */
export function QuickAccessGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: { xs: `${GRID_GAP.xs}px`, md: `${GRID_GAP.md}px` },
        '& > *': {
          flex: '0 0 auto',
          minWidth: 0,
          width: {
            xs: itemWidth('xs'),
            sm: itemWidth('sm'),
            md: itemWidth('md'),
            lg: itemWidth('lg'),
          },
        },
      }}
    >
      {children}
    </Box>
  );
}
